import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, IconButton, Chip, Skeleton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import InfoIcon from '@mui/icons-material/Info';

const NewsCarousel = () => {
    const [news, setNews] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const response = await fetch(process.env.REACT_APP_URL_NEWS, {
                    credentials: 'include'
                });
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    setNews(data);
                }
            } catch (error) {
                console.error('Errore nel caricamento delle news:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchNews();
    }, []);

    const nextSlide = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % news.length);
    }, [news.length]);

    const prevSlide = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + news.length) % news.length);
    }, [news.length]);

    // Auto-scroll ogni 5 secondi
    useEffect(() => {
        if (news.length <= 1 || isPaused) return;

        const interval = setInterval(() => {
            nextSlide();
        }, 5000);

        return () => clearInterval(interval);
    }, [news.length, isPaused, nextSlide]);

    const getTypeIcon = (type) => {
        switch (type) {
            case 'alert':
                return <NewReleasesIcon sx={{ fontSize: 18 }} />;
            case 'info':
                return <InfoIcon sx={{ fontSize: 18 }} />;
            default:
                return <AnnouncementIcon sx={{ fontSize: 18 }} />;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'alert':
                return { bg: '#fff3e0', color: '#e65100', chip: 'warning' };
            case 'info':
                return { bg: '#e3f2fd', color: '#1565c0', chip: 'info' };
            default:
                return { bg: '#fce4ec', color: '#a71e3b', chip: 'default' };
        }
    };

    if (loading) {
        return (
            <Paper
                elevation={0}
                sx={{
                    mb: 4,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Skeleton variant="text" width="30%" height={32} />
                    <Skeleton variant="text" width="100%" height={24} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width="80%" height={24} />
                </Box>
            </Paper>
        );
    }

    if (news.length === 0) {
        return null;
    }

    const currentNews = news[currentIndex];
    const typeStyle = getTypeColor(currentNews.type);

    return (
        <Paper
            elevation={0}
            sx={{
                mb: 4,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${typeStyle.bg} 0%, #ffffff 100%)`,
                transition: 'all 0.3s ease'
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* Header con titolo sezione */}
            <Box
                sx={{
                    px: 3,
                    py: 1.5,
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'rgba(255,255,255,0.7)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AnnouncementIcon sx={{ color: '#a71e3b', fontSize: 20 }} />
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 600,
                            color: '#333',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5
                        }}
                    >
                        News & Comunicazioni
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#666' }}>
                    {currentIndex + 1} / {news.length}
                </Typography>
            </Box>

            {/* Contenuto principale */}
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        p: 3,
                        minHeight: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}
                >
                    {/* Tipo e data */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Chip
                            icon={getTypeIcon(currentNews.type)}
                            label={currentNews.type === 'alert' ? 'Avviso' : currentNews.type === 'info' ? 'Info' : 'News'}
                            size="small"
                            color={typeStyle.chip}
                            sx={{
                                fontWeight: 500,
                                '& .MuiChip-icon': { color: 'inherit' }
                            }}
                        />
                        {currentNews.date && (
                            <Typography variant="caption" sx={{ color: '#888' }}>
                                {new Date(currentNews.date).toLocaleDateString('it-IT', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                })}
                            </Typography>
                        )}
                    </Box>

                    {/* Titolo */}
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            color: typeStyle.color,
                            mb: 1,
                            lineHeight: 1.3
                        }}
                    >
                        {currentNews.title}
                    </Typography>

                    {/* Descrizione */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#555',
                            lineHeight: 1.6
                        }}
                    >
                        {currentNews.description}
                    </Typography>

                    {/* Link opzionale */}
                    {currentNews.link && (
                        <Typography
                            component="a"
                            href={currentNews.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                mt: 1.5,
                                color: typeStyle.color,
                                textDecoration: 'none',
                                fontWeight: 500,
                                fontSize: '0.875rem',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Scopri di più →
                        </Typography>
                    )}
                </Box>

                {/* Frecce navigazione */}
                {news.length > 1 && (
                    <>
                        <IconButton
                            onClick={prevSlide}
                            sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255,255,255,0.9)',
                                boxShadow: 1,
                                '&:hover': {
                                    bgcolor: 'white',
                                    boxShadow: 2
                                }
                            }}
                            size="small"
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                            onClick={nextSlide}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255,255,255,0.9)',
                                boxShadow: 1,
                                '&:hover': {
                                    bgcolor: 'white',
                                    boxShadow: 2
                                }
                            }}
                            size="small"
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </>
                )}
            </Box>

            {/* Indicatori */}
            {news.length > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 0.5,
                        pb: 2,
                        pt: 1
                    }}
                >
                    {news.map((_, index) => (
                        <IconButton
                            key={index}
                            onClick={() => setCurrentIndex(index)}
                            sx={{
                                p: 0.25,
                                transition: 'transform 0.2s ease'
                            }}
                        >
                            <FiberManualRecordIcon
                                sx={{
                                    fontSize: index === currentIndex ? 12 : 8,
                                    color: index === currentIndex ? typeStyle.color : '#ccc',
                                    transition: 'all 0.3s ease'
                                }}
                            />
                        </IconButton>
                    ))}
                </Box>
            )}

            {/* Progress bar animata */}
            {news.length > 1 && !isPaused && (
                <Box
                    sx={{
                        height: 3,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            bgcolor: typeStyle.color,
                            animation: 'progress 5s linear infinite',
                            '@keyframes progress': {
                                '0%': { width: '0%' },
                                '100%': { width: '100%' }
                            }
                        }}
                    />
                </Box>
            )}
        </Paper>
    );
};

export default NewsCarousel;
